import { YESTERDAY_DATE_RANGE } from "app/utils/date";

import { useValidatedCubeQuery } from "../useValidatedCubeQuery";
import { createCombinedErrorFromMultiple } from "../utils";
import {
  sectionResourceStatsDistinctResourcesResultArraySchema,
  sectionResourceStatsResourceCountResultArraySchema,
} from "./useSectionResourceStats.schema";

import type { Query } from "@cubejs-client/core";

interface UseSectionResourceStatsParams {
  dashboardKey: string;
  sectionKey: string;
}

interface UseSectionResourceStatsResult {
  findingCount: number | null;
  resourceCount: number | null;
  isLoading: boolean;
  error: Error | null;
}

function buildDistinctResourcesQuery(
  dashboardKey: string,
  sectionKey: string,
  mostRecentDate: string,
): Query {
  return {
    measures: ["ControlSignal.distinctResources"],
    timeDimensions: [
      {
        dimension: "ControlSignal.date",
        granularity: "day",
        dateRange: mostRecentDate,
      },
    ],
    filters: [
      {
        member: "Dashboard.key",
        operator: "equals",
        values: [dashboardKey],
      },
      {
        member: "DashboardSection.key",
        operator: "equals",
        values: [sectionKey],
      },
    ],
    dimensions: ["Dashboard.key", "DashboardSection.key"],
  };
}

function buildResourceCountQuery(
  dashboardKey: string,
  sectionKey: string,
  mostRecentDate: string,
): Query {
  return {
    measures: ["ResourceCount.resourceCount"],
    filters: [
      {
        member: "Dashboard.key",
        operator: "equals",
        values: [dashboardKey],
      },
      {
        member: "DashboardSection.key",
        operator: "equals",
        values: [sectionKey],
      },
    ],
    timeDimensions: [
      {
        dimension: "ResourceCount.date",
        granularity: "day",
        dateRange: mostRecentDate,
      },
    ],
    dimensions: ["Dashboard.key", "DashboardSection.key"],
  };
}

export function useSectionResourceStats({
  dashboardKey,
  sectionKey,
}: UseSectionResourceStatsParams): UseSectionResourceStatsResult {
  // We use "Yesterday" here b/c we might not have ControlSignal data for today yet
  const mostRecentDate = YESTERDAY_DATE_RANGE;

  const distinctResourcesQuery = useValidatedCubeQuery(
    buildDistinctResourcesQuery(dashboardKey, sectionKey, mostRecentDate),
    sectionResourceStatsDistinctResourcesResultArraySchema,
  );

  const resourceCountQuery = useValidatedCubeQuery(
    buildResourceCountQuery(dashboardKey, sectionKey, mostRecentDate),
    sectionResourceStatsResourceCountResultArraySchema,
  );

  const distinctResults =
    distinctResourcesQuery.resultSet?.tablePivot()[0] ?? {};
  const findingCount = distinctResults["ControlSignal.distinctResources"]
    ? Number(distinctResults["ControlSignal.distinctResources"])
    : null;

  const countResults = resourceCountQuery.resultSet?.tablePivot()[0] ?? {};
  const resourceCount = countResults["ResourceCount.resourceCount"]
    ? Math.round(Number(countResults["ResourceCount.resourceCount"]))
    : null;

  const isLoading =
    distinctResourcesQuery.isLoading || resourceCountQuery.isLoading;

  const error = createCombinedErrorFromMultiple([
    {
      error: distinctResourcesQuery.error,
      validationError: distinctResourcesQuery.validationError,
      errorPrefix: "Distinct resources validation failed",
    },
    {
      error: resourceCountQuery.error,
      validationError: resourceCountQuery.validationError,
      errorPrefix: "Resource count validation failed",
    },
  ]);

  return {
    findingCount,
    resourceCount,
    isLoading,
    error,
  };
}
